import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { Button, Card, List, makeStyles, Typography } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { batch, useDispatch, useSelector } from "react-redux";

import { BaseDialog } from "../BaseDialog";
import {
  addSectionTag,
  removeSectionTag,
} from "../../../store/data/current-document/action-creators/sections";
import { deleteSectionTag } from "../../../store/data/current-document/action-creators/sectionTags";
import { RootState } from "../../../store/reducers";
import { Tag } from "../../../data/models/documentProperties/section";
import { sectionTagsSelector } from "../../../store/data/current-document/selectors/sectionTags";
import { Days } from "../../../data/models/documentProperties/day";
import { daysSelector } from "../../../store/data/current-document/selectors/days";
import { appTheme } from "../../../styling/style";
import { TagListItem } from "./components/TagListItem";
import { OpenDialogButton } from "../../common/OpenDialogButton";
import { SelectSectionsList } from "./components/SelectSectionsList";
import { SimpleCreateSectionTagDialog } from "./SimpleCreateSectionTagDialog";
import { ImportSectionTagsDialog } from "./ImportSectionTagsDialog";

const useStyles = makeStyles((theme) => ({
  sectionSelectorContainer: {
    display: "flex",
    flexDirection: "column",
  },
  selectButtonsContainer: {
    display: "flex",
  },
  sectionsList: {
    height: 300,
    overflowY: "auto",
  },
  root: {
    display: "flex",
    flexDirection: "column",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  addIcon: {
    color: appTheme.colors.primary,
  },
}));

interface SectionTagManagerDialogProps {
  onClose: () => void;
}

export const SectionTagManagerDialog: FunctionComponent<SectionTagManagerDialogProps> = ({
  onClose,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const onAddSectionTag = (
    dayIndex: number,
    sectionIndex: number,
    tagId: string
  ) => dispatch(addSectionTag(dayIndex, sectionIndex, tagId));

  const onRemoveSectionTag = (
    dayIndex: number,
    sectionIndex: number,
    tagId: string
  ) => dispatch(removeSectionTag(dayIndex, sectionIndex, tagId));

  const onDeleteSectionTag = (tagId: string) =>
    dispatch(deleteSectionTag(tagId));

  const documentSectionTags: Tag[] = useSelector<RootState, Tag[]>(
    sectionTagsSelector
  );

  const days: Days = useSelector<RootState, Days>(daysSelector);

  const [selectedSections, setSelectedSections] = useState<
    { day: number; section: number }[]
  >([]);
  const [selectedTag, setSelectedTag] = useState("");
  const [showImportTagsDialog, setShowImportTagsDialog] = useState(false);
  const [showCreateTagDialog, setShowCreateTagDialog] = useState(false);
  const [showEditTagDialog, setShowEditTagDialog] = useState(false);

  useEffect(() => {
    if (selectedTag) {
      const sections = days.getSectionsWithTag([selectedTag]);
      setSelectedSections(sections);
    }
  }, [selectedTag, setSelectedSections, days]);

  const onClickDelete = (tagId: string) => {
    batch(() => {
      for (const position of days.getSectionsWithTag([tagId])) {
        onRemoveSectionTag(position.day, position.section, tagId);
      }
      onDeleteSectionTag(tagId);
    });
    if (selectedTag === tagId) {
      setSelectedTag("");
      setSelectedSections([]);
    }
  };

  const onSelectTag = (tagId: string) => {
    selectedTag === tagId ? setSelectedTag("") : setSelectedTag(tagId);
  };

  const onClickEditTag = (tagId: string) => {
    if (selectedTag !== tagId) {
      setSelectedTag(tagId);
    }
    setShowEditTagDialog(true);
  };

  const createTagButton: ReactNode = (
    <Button
      data-cy="createSectionTag"
      startIcon={<Add className={classes.addIcon} />}
      onClick={() => setShowCreateTagDialog(true)}
      size="small"
    >
      Create section tag
    </Button>
  );

  const currentDocumentTags = (
    <Card>
      <List className={classes.sectionsList}>
        {createTagButton}
        {documentSectionTags.map(
          (tag: Tag): ReactNode => (
            <TagListItem
              sectionTag={tag}
              isSelected={tag.id === selectedTag}
              onSelectTag={onSelectTag}
              onDeleteTag={onClickDelete}
              onEditTag={onClickEditTag}
            />
          )
        )}
      </List>
    </Card>
  );

  const haveSectionTagsChanged = (): boolean => {
    const sectionsWithTags = days.getSectionsWithTag(
      selectedTag ? [selectedTag] : []
    );
    if (sectionsWithTags.length !== selectedSections.length) {
      return true;
    }
    for (const position of selectedSections) {
      if (
        !sectionsWithTags.find(
          (section: { day: number; section: number }): boolean =>
            section.day === position.day && section.section === position.section
        )
      ) {
        return true;
      }
    }
    return false;
  };

  const onApplySectionTags = () => {
    batch(() => {
      for (const position of days.getSectionsWithTag([selectedTag])) {
        onRemoveSectionTag(position.day, position.section, selectedTag);
      }
    });

    if (selectedSections.length) {
      batch(() => {
        for (const section of selectedSections) {
          onAddSectionTag(section.day, section.section, selectedTag);
        }
      });
    }
  };

  const onSaveSectionTags = () => {
    onApplySectionTags();
    onClose();
  };

  const importTagsButton: ReactNode = (
    <OpenDialogButton
      onClick={() => setShowImportTagsDialog(true)}
      label="Import section tags"
    />
  );

  const dialogActions: ReactNode = [
    <Button onClick={onClose}>Close</Button>,
    <Button
      color="secondary"
      disabled={!selectedTag || !haveSectionTagsChanged()}
      onClick={onApplySectionTags}
    >
      Apply
    </Button>,
    <Button color="secondary" onClick={onSaveSectionTags}>
      OK
    </Button>,
  ];

  const body: ReactNode = (
    <>
      <div className={classes.root}>
        <Typography>Create, import and apply tags to sections in this document.</Typography>
        <br/>
        <div>
          <div className={classes.headerContainer}>
            <Typography variant="h3">Available section tags</Typography>
            {importTagsButton}
          </div>
          {currentDocumentTags}
        </div>
        <div>
          <SelectSectionsList
            selectedSections={selectedSections}
            setSelectedSections={setSelectedSections}
            tagId={selectedTag || undefined}
            disabled={!selectedTag}
          />
        </div>
      </div>
      {(showCreateTagDialog || showEditTagDialog) && (
        <SimpleCreateSectionTagDialog
          onClose={() =>
            showEditTagDialog
              ? setShowEditTagDialog(false)
              : setShowCreateTagDialog(false)
          }
          tagId={(showEditTagDialog && selectedTag) || undefined}
          isEditing={showEditTagDialog}
        />
      )}
      {showImportTagsDialog && (
        <ImportSectionTagsDialog
          onClose={() => setShowImportTagsDialog(false)}
        />
      )}
    </>
  );

  return (
    <BaseDialog
      onClose={onClose}
      open={true}
      title="Manage section tags"
      body={body}
      action={dialogActions}
      maxWidth="sm"
    ></BaseDialog>
  );
};
